import { useCallback, useEffect, useState, useMemo } from "react";
import { offices as officesService } from "../../../api/offices.service";
import type { Office } from "../../../api/offices.service";

/**
 * Manages office options for a specific firm, filtered by country name.
 * Offices are only fetched when BOTH firmId AND countryName are available.
 * For Elite packages, offices are filtered to match the placement region's country.
 */
export function useOfficeOptions(
  initialFirmId: number | string | null | undefined,
  countryName?: string | null,
) {
  const [selectedFirmId, setSelectedFirmId] = useState<number | null>(
    initialFirmId ? Number(initialFirmId) : null,
  );
  const [allOffices, setAllOffices] = useState<Office[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOffices = useCallback(async (firmId: number) => {
    setIsLoading(true);
    try {
      // Fetch with high limit to ensure we get all offices for filtering
      const response = await officesService.getAll({ companyWpId: firmId, limit: 500 });
      setAllOffices(response.offices);
    } catch (error) {
      console.error("Failed to fetch offices:", error);
      setAllOffices([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Filter offices by country name client-side
  const officeOptions = useMemo(() => {
    // Don't show options if no country is selected
    if (!countryName) {
      return [];
    }
    const filtered = allOffices.filter(
      office => office.countryName?.toLowerCase() === countryName.toLowerCase(),
    );
    return filtered.map(office => {
      const name = office.name || `Office ${office.officeId}`;
      const location = [office.city, office.countryName].filter(Boolean).join(", ");
      return {
        value: office.officeId,
        label: location ? `${name} (${location})` : name,
        description: `ID: ${office.officeId}`,
      };
    });
  }, [allOffices, countryName]);

  // Fetch offices when firmId changes AND we have a country selected
  useEffect(() => {
    if (selectedFirmId && countryName) {
      fetchOffices(selectedFirmId);
    }
  }, [selectedFirmId, countryName, fetchOffices]);

  // Handle initial firm in edit mode
  useEffect(() => {
    if (initialFirmId) {
      setSelectedFirmId(Number(initialFirmId));
    }
  }, [initialFirmId]);

  const handleFirmChange = useCallback((firmId: number) => {
    setSelectedFirmId(firmId);
    // Offices will be fetched by the useEffect when countryName is also set
  }, []);

  return {
    selectedFirmId,
    officeOptions,
    isLoading,
    handleFirmChange,
    fetchOffices,
  };
}
